import axios, { AxiosRequestConfig } from 'axios'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { API_RESPONSE_TYPE } from '../../constants'
import { getGoogleDriveAccessToken, IGoogleDriveToken } from './GoogleDriveApi'

const UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart'
const FILES_URL = 'https://www.googleapis.com/drive/v3/files'
const ROOT_FOLDER_NAME = 'okcredit'
const MIME_TYPE_FOLDER = 'application/vnd.google-apps.folder'

const publicUrl = (fileId: string) => `https://drive.google.com/uc?export=view&id=${fileId}`

let cachedToken: IGoogleDriveToken | null = null
let cachedRootFolderId: string | null = null

const isTokenExpired = (token: IGoogleDriveToken): boolean => {
  const expiryMs = Date.parse(token.expiry)
  if (Number.isNaN(expiryMs)) {
    return true
  }
  return Date.now() >= expiryMs - 30 * 1000
}

const getValidToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh && cachedToken && !isTokenExpired(cachedToken)) {
    return cachedToken.access_token
  }
  const response = await getGoogleDriveAccessToken()
  if (response.type !== API_RESPONSE_TYPE.SUCCESS || !response.data?.access_token) {
    throw new Error('Unable to fetch Google Drive access token')
  }
  cachedToken = response.data
  return cachedToken.access_token
}

const authConfig = (accessToken: string, extra: AxiosRequestConfig = {}): AxiosRequestConfig => ({
  ...extra,
  headers: {
    Authorization: `Bearer ${accessToken}`,
    ...(extra.headers || {})
  }
})

const findRootFolder = async (accessToken: string): Promise<string | null> => {
  const q =
    `name='${ROOT_FOLDER_NAME}' and 'root' in parents and 'me' in owners` +
    ` and mimeType='${MIME_TYPE_FOLDER}' and trashed=false`
  const response = await axios.get(FILES_URL, authConfig(accessToken, { params: { q } }))
  const files = response.data?.files || []
  return files.length > 0 ? files[0].id : null
}

const createRootFolder = async (accessToken: string): Promise<string> => {
  const response = await axios.post(
    FILES_URL,
    { name: ROOT_FOLDER_NAME, mimeType: MIME_TYPE_FOLDER },
    authConfig(accessToken, { headers: { 'Content-Type': 'application/json' } })
  )
  return response.data.id
}

const getRootFolderId = async (accessToken: string): Promise<string> => {
  if (cachedRootFolderId) {
    return cachedRootFolderId
  }
  const existing = await findRootFolder(accessToken)
  cachedRootFolderId = existing || (await createRootFolder(accessToken))
  return cachedRootFolderId
}

const buildMultipartBlob = (metadata: object, file: File): { body: Blob; boundary: string } => {
  const boundary = `gdrive_${Date.now()}_${Math.random().toString(36).slice(2)}`
  const delimiter = `\r\n--${boundary}\r\n`
  const closeDelimiter = `\r\n--${boundary}--\r\n`

  const head =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${JSON.stringify(metadata)}` +
    delimiter +
    `Content-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`

  const body = new Blob([head, file, closeDelimiter], {
    type: `multipart/related; boundary=${boundary}`
  })
  return { body, boundary }
}

const uploadFileToDrive = async (
  file: File,
  parentFolderId: string,
  accessToken: string,
  fileReference?: string
): Promise<string> => {
  const metadata: Record<string, any> = {
    name: file.name,
    parents: [parentFolderId]
  }
  if (fileReference) {
    metadata.properties = { fileReference }
  }

  const { body, boundary } = buildMultipartBlob(metadata, file)
  const response = await axios.post(UPLOAD_URL, body, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': `multipart/related; boundary=${boundary}`
    }
  })
  const fileId: string = response.data?.id
  if (!fileId) {
    throw new Error('Google Drive did not return a file id')
  }
  return fileId
}

const setPublicPermission = async (fileId: string, accessToken: string): Promise<void> => {
  await axios.post(
    `${FILES_URL}/${fileId}/permissions`,
    { role: 'reader', type: 'anyone' },
    authConfig(accessToken, { headers: { 'Content-Type': 'application/json' } })
  )
}

export interface IDriveUploadResult {
  url: string
  fileId: string
}

export type TDriveUploadStep = 'token' | 'folder' | 'upload' | 'permission'
const extractGoogleError = (error: any): { reason: string; message: string } => {
  const googleError = error?.response?.data?.error
  const reason: string =
    googleError?.errors?.[0]?.reason || googleError?.status || googleError?.reason || ''
  const message: string =
    googleError?.message || (typeof googleError === 'string' ? googleError : '') || ''
  return { reason, message }
}

// Same per-upload events the mobile apps emit from their Drive syncer (MerchantApplication /
// MerchantApp: new_file_upload_started / _completed / _failed), so one dashboard covers all three.
const trackUpload = (event: string, props: Record<string, unknown>) => {
  try {
    Mixpanel.trackWithPreset(event, props)
  } catch (e) {
    // analytics must never break an upload
  }
}

export const uploadFileToGoogleDrive = async (
  file: File,
  fileReference?: string
): Promise<IDriveUploadResult> => {
  const maxAttempts = 2
  let lastError: any
  const fileRef = fileReference || ''

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // Mobile counts attempts from 1
    trackUpload('new_file_upload_started', {
      file_reference: fileRef,
      file_id: '',
      attempt: attempt + 1
    })
    let step: TDriveUploadStep = 'token'
    try {
      const accessToken = await getValidToken(attempt > 0)
      step = 'folder'
      const folderId = await getRootFolderId(accessToken)
      step = 'upload'
      const fileId = await uploadFileToDrive(file, folderId, accessToken, fileReference)
      step = 'permission'
      await setPublicPermission(fileId, accessToken)
      const url = publicUrl(fileId)
      trackUpload('new_file_upload_completed', {
        file_reference: fileRef,
        remote_url: url,
        file_id: fileId,
        attempt: attempt + 1
      })
      return { url, fileId }
    } catch (error: any) {
      lastError = error
      if (error && typeof error === 'object') {
        error.driveStep = step
      }
      const status = error?.response?.status
      const { reason, message } = extractGoogleError(error)
      const staleFolder = status === 404 && step === 'upload'
      const retryable = status === 401 || staleFolder
      const terminal = !retryable || attempt === maxAttempts - 1
      trackUpload('new_file_upload_failed', {
        file_reference: fileRef,
        error: describeGoogleDriveError(error),
        error_class: error?.name || 'Error',
        http_status: status || 0,
        google_reason: reason,
        google_message: message,
        step,
        file_id: '',
        attempt: attempt + 1,
        terminal
      })
      if (!retryable) {
        throw error
      }
      if (status === 401) {
        cachedToken = null
      }
      cachedRootFolderId = null
    }
  }

  throw lastError
}

export const uploadFilesToGoogleDrive = async (
  files: { file: File; requestId: string }[]
): Promise<IDriveUploadResult[]> => {
  return Promise.all(files.map(({ file, requestId }) => uploadFileToGoogleDrive(file, requestId)))
}
export const describeGoogleDriveError = (error: any): string => {
  const status: number | undefined = error?.response?.status
  const step: TDriveUploadStep | undefined = error?.driveStep
  const { reason, message: googleMessage } = extractGoogleError(error)

  if (!status) {
    if (/access token/i.test(error?.message || '')) {
      return 'Could not connect to Google Drive, please reconnect Drive in Settings'
    }
    return error?.message ? `Network error: ${error.message}` : 'Network error'
  }
  if (status === 401) {
    return 'Google Drive access has expired, please reconnect Drive in Settings'
  }
  if (status === 403) {
    if (/storageQuotaExceeded|quota/i.test(reason) || /quota/i.test(googleMessage)) {
      return 'Google Drive storage is full'
    }
    if (step === 'permission') {
      return 'Google Drive would not allow the bill to be shared by link'
    }
    if (/insufficientFilePermissions|insufficientPermissions|forbidden/i.test(reason)) {
      return 'No permission to write to the okcredit folder in Google Drive'
    }
  }
  if (status === 404) {
    return 'The okcredit folder was not found in Google Drive, please try again'
  }
  const detail = googleMessage || reason || error?.message || ''
  return detail ? `Google Drive error ${status}: ${detail}` : `Google Drive error ${status}`
}

export const clearGoogleDriveCache = (): void => {
  cachedToken = null
  cachedRootFolderId = null
}
